import {createApp, PropType, ref} from 'vue';
import {connectVue} from '../modules/configureBugsnag';
import configureGlobalComponents from '../modules/configureGlobalComponents';
import axiosClient from '../modules/axiosClient';
import CarSearchFinanceInfo from '../components/car-search/car-search-finance-info.vue';
import CarSearchListing from '../components/car-search/car-search-listing.vue';
import CarSearchPagination from '../components/car-search/car-search-pagination.vue';
import CarSearchRefine from '../components/car-search/car-search-refine.vue';
import adverts from '../modules/adverts';
import formatThousandsWithRounding from '../modules/formatThousandsWithRounding';
import generateSearchUrl from '../modules/generateSearchUrl';
import {ApiStarredVehicle} from '../../typings/api';
// @ts-ignore
import VueAgile from '../components/slider/Agile.vue';
import useVuelidate from '@vuelidate/core';
import {helpers, required} from '@vuelidate/validators';
import {postcode} from '../modules/regitValidators';


const app = createApp({
    components: {
        CarSearchListing,
        CarSearchPagination,
        CarSearchRefine,
        'finance-info': CarSearchFinanceInfo,
        'agile': VueAgile,
    },
    props: {
        'seoPage': {type: Boolean},
        'refineData': {type: Object},
        'dropdownOptions': {type: Object},
        'initialCurrentPage': {type: Number},
        'initialTotalPages': {type: Number},
        'initialTotalResults': {type: Number},
        'initialSort': {type: String},
        'partner': {type: String},
        'originalStarredVehicles': {type: Array as PropType<ApiStarredVehicle[]>, default: () => []},
    },
    data() {
        return {
            // result display
            adTargeting: {},
            offerType: 'used',
            priceType: 'cash',
            loadingResults: false,
            results: [],

            // current cursor, manages dynamic results
            currentSort: 'asc',
            currentUrl: '/used-cars/all',
            currentPage: 1,
            currentTotalPages: 1,
            currentTotalResults: 0,

            // fields needed to understand if navigated away from Blade view
            originalUrl: '',
            originalCurrentPage: 1,
            originalSort: '',

            // modal finance info fields
            currentHpInfo: undefined,
            currentPcpInfo: undefined,


            // current starred vehicle ID's
            starredVehicles: [] as ApiStarredVehicle[],
        };
    },
    setup(props) {
        const state = {
            dealerPostcode: ref(''),
        };

        const rules = {
            dealerPostcode: {
                required: helpers.withMessage('Please enter your postcode', required),
                postcode: helpers.withMessage('Value is not a valid postcode', postcode),
            },
        };

        const v = useVuelidate(rules, state);
        async function submitDealerSearch(e: Event) {
            e.preventDefault();
            e.stopPropagation();

            const validationPassed = await v.value.$validate();
            if (validationPassed) {
                (document.getElementById('dealer-search') as HTMLFormElement).submit();
            }
        }

        return {
            dealerPostcode: state.dealerPostcode,
            v$: v,
            submitDealerSearch,
        };
    },
    methods: {
        toggleStarred(id: string) {
            const starredVehicle = this.starredVehicles.find((item: ApiStarredVehicle) => item.externalId === id);

            if (!window.userLoggedIn) {
                const currentPageUrl = generateSearchUrl(this.currentUrl, this.currentPage, this.currentSort);

                window.location.href = '/login?referring_url=' + btoa(currentPageUrl);
                return;
            }

            if (starredVehicle) {
                this.unStarVehicle(starredVehicle);
            } else {
                this.starVehicle(id);
            }
        },
        starVehicle(id: string) {
            console.log('star vehicle', id);
            axiosClient.post('/api/starred-vehicles', {
                type: 'used',
                externalId: id,
            }).then((result) => {
                this.starredVehicles.push(result.data as ApiStarredVehicle);
            }).catch(() => {
                console.log('error starring vehicle');
            }).finally(() => {
                if (this.initialLoad) {
                    window.location.reload();
                }
            });
        },
        unStarVehicle(starredVehicle: ApiStarredVehicle) {
            console.log('unstar vehicle', starredVehicle);

            axiosClient.delete('/api/starred-vehicles/' + starredVehicle.id).then((result) => {
                const index = this.starredVehicles.indexOf(starredVehicle);

                if (index > -1) {
                    this.starredVehicles.splice(index, 1);
                }
            }).catch(() => {
                console.log('error un-starring vehicle');
            }).finally(() => {
                if (this.initialLoad) {
                    window.location.reload();
                }
            });
        },
        setFinanceInfoModalData(event: any) {
            this.currentHpInfo = event.hp;
            this.currentPcpInfo = event.pcp;
        },
        changeSortBy() {
            this.currentPage = 1;
            this.loadAdverts();
        },
        changePage(page: number) {
            if (this.currentPage === page) {
                return;
            }

            this.currentPage = page;
            this.loadAdverts();
        },
        initCurrentUrl(payload: any) {
            this.currentUrl = payload.url;
            this.originalUrl = payload.url;
        },
        changeUrl(payload: any) {
            if (this.currentUrl === payload.url) {
                // reset cursor to first page if no filter changes
                this.changePage(1);
                return;
            }
            this.currentPage = 1;
            this.currentUrl = payload.url;
            this.currentTotalPages = payload.totalPages;
            this.currentTotalResults = payload.totalResults;
            this.adTargeting = payload.adTargeting;
            this.priceType = payload.priceType;
            this.offerType = payload.offerType;
            this.loadAdverts();
        },
        loadAdverts(pushIntoHistory = true, scrollToTop = true) {
            const searchUrl = generateSearchUrl(this.currentUrl, this.currentPage, this.currentSort);
            const apiUrl = '/api/car-search/results' + searchUrl;

            // redirect to search page if we are on a SEO page
            if (this.seoPage) {
                window.location.href = searchUrl;
            }

            this.loadingResults = true;
            if (scrollToTop) {
                this.scrollToStart();
            }
            axiosClient.get(apiUrl).then((response) => {
                if (pushIntoHistory) {
                    history.pushState({
                        previousUrl: this.currentUrl,
                        previousPage: this.currentPage,
                        previousSort: this.currentSort,
                    }, '', searchUrl);
                }
                this.results = response.data.result;
                this.loadingResults = false;
                this.refreshAds();
            }, (response) => {
                console.error(response);
                this.results = [];
                this.loadingResults = false;
            });
        },
        loadHistory(event: PopStateEvent) {
            if (event.state === null) {
                this.currentUrl = this.originalUrl;
                this.currentPage = this.originalCurrentPage;
                this.currentSort = this.originalSort;
            } else {
                this.currentUrl = event.state.previousUrl;
                this.currentPage = event.state.previousPage;
                this.currentSort = event.state.previousSort;
            }
            this.loadAdverts(false);
        },
        refreshAds() {
            adverts.retargetAdverts(this.adTargeting);
        },
        formatThousandsWithRounding,
        scrollToStart() {
            window.scrollTo(0, 0);

            if (this.currentPage > 1) {
                const appElement = document.getElementById('app');
                if (appElement) {
                    appElement.scrollIntoView();
                }
            }
        },
    },
    mounted() {
        this.offerType = this.refineData.cars || 'used';
        this.priceType = this.refineData.pricetype || 'cash';

        // this.currentUrl - updated by hook from refine options mounted() event
        this.currentSort = this.initialSort;
        this.currentPage = this.initialCurrentPage;
        this.currentTotalPages = this.initialTotalPages;
        this.currentTotalResults = this.initialTotalResults;

        // this.originalUrl - updated by hook from refine options mounted() event
        this.originalSort = this.initialSort;
        this.originalCurrentPage = this.initialCurrentPage;

        window.addEventListener('popstate', this.loadHistory);
        this.starredVehicles = JSON.parse(JSON.stringify(this.originalStarredVehicles));
    },
    computed: {
        initialLoad(): boolean {
            return this.originalCurrentPage === this.currentPage &&
            this.originalUrl === this.currentUrl &&
            this.originalSort === this.currentSort;
        },
        starredIds(): string[] {
            return this.starredVehicles.map((item: ApiStarredVehicle) => item.externalId || '');
        },
    },
}, window.propsData);

connectVue(app);
configureGlobalComponents(app);
app.mount('#app');


